import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Tooltip } from '../Tooltip';
import { AppIcon } from '../AppIcon';
import type { ComponentChildren } from 'preact';
import type { PdfPageGeometry } from '../../../shared/pdf/geometry';
import type { PdfGenerationResult } from '../../../shared/pdf/generate';
import type { PdfDocumentDefinition } from '../../../shared/types';
import { pageViewport, type PdfViewport } from '../../../shared/pdf/geometry';
import { loadOrganizerPdf, pdfActionError, type OrganizerPdfSource } from '../../lib/pdfForms';
import { ApiError } from '../../lib/api';
import { reportClientError } from '../../lib/clientErrors';
import { openPdfPreview, placementOverlayStyle, type PdfPreview } from '../../pdf/render';

export interface ViewerPage { viewport: PdfViewport; pageIndex: number; source: OrganizerPdfSource }
interface Props {
  eventId: string;
  /** The organizer's form shown, if it is one; without it the viewer shows `generated`, such as the signed permission form. */
  document?: PdfDocumentDefinition;
  generated?: PdfGenerationResult;
  loadSource?: (signal: AbortSignal) => Promise<OrganizerPdfSource>;
  navigationKey?: number;
  requestedPage?: number;
  overlay?: (page: ViewerPage) => ComponentChildren;
  onSource?: (source: OrganizerPdfSource | undefined) => void;
  onPage?: (page: ViewerPage | undefined) => void;
  /** The page's text, for screen readers. Families get it; the organizer's editor and example leave it out. */
  transcript?: boolean;
  /** More buttons after Zoom in, in the same row, such as Undo and Redo. */
  controls?: ComponentChildren;
  /** Buttons in the middle of the row, such as text alignment for the selected fields. */
  tools?: ComponentChildren;
  /** Buttons at the very start of the row, before the page buttons, such as adding a field of one kind. */
  leading?: ComponentChildren;
  /** Something laid over the PDF's view, such as choosing what a new field will show. */
  cover?: ComponentChildren;
  /** While something over the PDF needs an answer first, the controls row can't be used. */
  locked?: boolean;
}
const ZOOMS = [.25, .5, .75, 1, 1.5, 2, 3, 4];
export function PdfPageViewer({ eventId, document, generated, loadSource, navigationKey, requestedPage, overlay, onSource, onPage, transcript = true, controls, tools, leading, cover, locked }: Props) {
  const [source, setSource] = useState<OrganizerPdfSource>(), [session, setSession] = useState<PdfPreview>();
  const [pageIndex, setPageIndex] = useState(0), [zoom, setZoom] = useState('fit');
  const [viewport, setViewport] = useState<PdfViewport>(), [text, setText] = useState('');
  const [error, setError] = useState(''), [retry, setRetry] = useState(0), [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(600);
  const canvas = useRef<HTMLCanvasElement>(null), container = useRef<HTMLDivElement>(null), stage = useRef<HTMLDivElement>(null);
  const sourceIdentity = useRef('');
  const knownPages = useRef<PdfPageGeometry[]>([]);
  const callbacks = useRef({ onSource, onPage }); callbacks.current = { onSource, onPage };
  // Fit width follows the space the page has. It is measured after every render as well as on resize, so the width is
  // never left at the first guess, for example after the app reloads in development and the viewer starts over.
  const measure = () => { const element = container.current; if (element) setWidth(Math.max(100, element.clientWidth || 600)); };
  useLayoutEffect(measure);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !container.current) return;
    const observer = new ResizeObserver(measure); observer.observe(container.current); return () => observer.disconnect();
  }, []);
  useEffect(() => {
    let active = true, preview: PdfPreview | undefined;
    const controller = new AbortController();
    setSource(undefined); setSession(undefined); setViewport(undefined); setText(''); setError(''); setLoading(true);
    const identity = JSON.stringify([eventId, document?.id, document?.assetId, document?.templateSha256]);
    if (identity !== sourceIdentity.current) { setPageIndex(0); knownPages.current = []; }
    sourceIdentity.current = identity;
    callbacks.current.onSource?.(undefined); callbacks.current.onPage?.(undefined);
    void (async () => {
      try {
        const loaded = generated ? { bytes: generated.bytes, pages: generated.pages, nativeFields: [] } : await (loadSource ? loadSource(controller.signal) : loadOrganizerPdf(eventId, document!, controller.signal));
        if (!active) return;
        preview = await openPdfPreview(loaded.bytes);
        if (!active) { await preview.dispose(); return; }
        knownPages.current = loaded.pages; setSource(loaded); setPageIndex(i => Math.min(i, loaded.pages.length - 1)); setSession(preview); callbacks.current.onSource?.(loaded);
      } catch (e) {
        if (!active) return;
        // The server's refusals explain themselves; anything else is reported, since the message shown is general.
        if (!(e instanceof ApiError)) reportClientError('pdf viewer: open', e, { eventId, documentId: document?.id, generated: !!generated });
        setError(pdfActionError(e)); setLoading(false);
      }
    })();
    return () => { active = false; controller.abort(); callbacks.current.onSource?.(undefined); callbacks.current.onPage?.(undefined); void preview?.dispose(); };
  }, [eventId, document?.assetId, document?.templateSha256, generated, loadSource, retry]);
  useEffect(() => {
    if (source && requestedPage !== undefined && source.pages[requestedPage]) setPageIndex(requestedPage);
  }, [requestedPage, navigationKey, source]);
  const natural = source && pageViewport(source.pages[pageIndex]);
  const fit = Math.max(.1, Math.min(4, (width - 2) / (natural?.width || 612)));
  const scale = zoom === 'fit' ? fit : Number(zoom);
  // Fitting the width is one of the zoom steps, so zooming in and back out returns to it, and it keeps following the
  // viewer's width from there.
  const steps = [...ZOOMS.filter(z => Math.abs(z - fit) > .001), fit].sort((a, b) => a - b);
  // Zooming keeps the point of the page at the middle of what's in view at the middle, even as the box it scrolls in
  // grows or shrinks; only at the page's edges, where it can't scroll further, does it move off the middle.
  // A pinch keeps the point between the fingers under them instead, at x and y on screen.
  const anchor = useRef<{ fx: number; fy: number; x?: number; y?: number }>(undefined);
  /** The middle of the part of the page's box that's in the window, on screen. */
  const middle = (box: HTMLElement) => {
    const b = box.getBoundingClientRect();
    const left = Math.max(b.left, 0), right = Math.min(b.left + box.clientWidth, window.innerWidth);
    const top = Math.max(b.top, 0), bottom = Math.min(b.top + box.clientHeight, window.innerHeight);
    return { x: (left + right) / 2, y: (top + bottom) / 2 };
  };
  const zoomTo = (next: number, at?: { fx: number; fy: number; x: number; y: number }) => {
    const box = container.current, page = box?.firstElementChild?.getBoundingClientRect();
    if (at) anchor.current = at;
    else if (box && page?.width && page.height) {
      const { x, y } = middle(box), clamp = (v: number) => Math.min(1, Math.max(0, v));
      anchor.current = { fx: clamp((x - page.left) / page.width), fy: clamp((y - page.top) / page.height) };
    }
    setZoom(Math.abs(next - fit) <= .001 ? 'fit' : String(next));
  };
  useLayoutEffect(() => {
    // The page drawn at its new size replaces a pinch's stretched one.
    if (viewport && stage.current) stage.current.style.transform = '';
    const point = anchor.current, box = container.current, page = stage.current;
    if (!point || !viewport || !box || !page) return;
    anchor.current = undefined;
    const at = page.getBoundingClientRect(), { x, y } = point.x === undefined ? middle(box) : { x: point.x, y: point.y! };
    box.scrollLeft += at.left + point.fx * at.width - x; box.scrollTop += at.top + point.fy * at.height - y;
  }, [viewport]);
  // On a touch screen, two fingers pinch to zoom, in place of the zoom buttons. The page stretches as the fingers move, and
  // is drawn again at its new size when they lift.
  const latestZoom = useRef({ scale, fit, steps, zoomTo }); latestZoom.current = { scale, fit, steps, zoomTo };
  useEffect(() => {
    const box = container.current; if (!box) return;
    let pinch: { distance: number; x: number; y: number; fx: number; fy: number; ratio: number; to: { x: number; y: number } } | undefined;
    const touching = new Map<number, Element>();
    const pressed = (e: PointerEvent) => { if (e.pointerType === 'touch') touching.set(e.pointerId, e.target as Element); };
    const lifted = (e: PointerEvent) => { touching.delete(e.pointerId); };
    const spread = (touches: TouchList) => {
      const [a, b] = [touches[0], touches[1]];
      return { distance: Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)), x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
    };
    const start = (e: TouchEvent) => {
      const page = stage.current;
      if (e.touches.length !== 2 || !page?.offsetWidth) return;
      e.preventDefault();
      // A field the first finger began to drag goes back where it was: letting go of its pointer cancels the drag.
      for (const [id, target] of touching) if (target.hasPointerCapture?.(id)) target.releasePointerCapture(id);
      const { distance, x, y } = spread(e.touches), at = page.getBoundingClientRect();
      pinch = { distance, x, y, fx: (x - at.left) / at.width, fy: (y - at.top) / at.height, ratio: 1, to: { x, y } };
      page.style.transformOrigin = `${x - at.left}px ${y - at.top}px`;
    };
    const move = (e: TouchEvent) => {
      const page = stage.current;
      if (!pinch || e.touches.length !== 2 || !page) return;
      e.preventDefault();
      const { distance, x, y } = spread(e.touches), { scale, steps } = latestZoom.current;
      pinch.ratio = Math.min(steps.at(-1)! / scale, Math.max(steps[0] / scale, distance / pinch.distance));
      pinch.to = { x, y };
      page.style.transform = `translate(${x - pinch.x}px, ${y - pinch.y}px) scale(${pinch.ratio})`;
    };
    const end = (e: TouchEvent) => {
      if (!pinch || e.touches.length >= 2) return;
      const { fx, fy, ratio, to } = pinch, { scale, fit, zoomTo } = latestZoom.current;
      pinch = undefined;
      // Close to the page's width, it settles on fitting the width, which then follows the viewer's size.
      const next = Math.abs(scale * ratio / fit - 1) < .05 ? fit : scale * ratio;
      if (Math.abs(next - scale) < .001) { if (stage.current) stage.current.style.transform = ''; return; }
      zoomTo(next, { fx, fy, ...to });
    };
    // The browser's own pinch would zoom the whole page instead; Safari also sends its own gesture events for it.
    const gesture = (e: Event) => e.preventDefault();
    box.addEventListener('pointerdown', pressed, true);
    for (const type of ['pointerup', 'pointercancel'] as const) box.addEventListener(type, lifted, true);
    box.addEventListener('touchstart', start, { passive: false });
    box.addEventListener('touchmove', move, { passive: false });
    for (const type of ['touchend', 'touchcancel'] as const) box.addEventListener(type, end);
    box.addEventListener('gesturestart', gesture);
    return () => {
      box.removeEventListener('pointerdown', pressed, true);
      for (const type of ['pointerup', 'pointercancel'] as const) box.removeEventListener(type, lifted, true);
      box.removeEventListener('touchstart', start); box.removeEventListener('touchmove', move);
      for (const type of ['touchend', 'touchcancel'] as const) box.removeEventListener(type, end);
      box.removeEventListener('gesturestart', gesture);
    };
  }, []);
  useEffect(() => {
    if (!session || !source || !canvas.current) return;
    let active = true; const element = canvas.current;
    setLoading(true); setViewport(undefined); setText(''); setError(''); callbacks.current.onPage?.(undefined);
    void (async () => {
      const details = () => ({ eventId, documentId: document?.id, generated: !!generated, pageIndex, pageCount: session.pageCount, scale, bytes: source.bytes.length, canvas: [element.width, element.height] });
      try {
        const result = await session.renderPage(element, pageIndex, scale);
        if (!active || result.status === 'cancelled') return;
        setViewport(result.viewport); setLoading(false); callbacks.current.onPage?.({ viewport: result.viewport, pageIndex, source });
      } catch (e) {
        if (!active) return;
        reportClientError('pdf viewer: render', e, details());
        setError(`Could not render page ${pageIndex + 1}. Retry loading the blank source.`); setLoading(false);
        return;
      }
      // The page's text is only for screen readers; without it the page is still shown.
      if (!transcript) return;
      try { const content = await session.pageText(pageIndex); if (active) setText(content); }
      catch (e) { if (active) reportClientError('pdf viewer: text', e, details()); }
    })();
    return () => { active = false; void session.release(element); };
  }, [session, source, pageIndex, scale, transcript]);
  const pages = source?.pages ?? knownPages.current;
  return <div class="stack pdf-viewer" data-zoom={zoom}>
    <div class={`row wrap pdf-page-controls${tools ? ' with-tools' : ''}${locked ? ' locked' : ''}`} role="group" aria-label="PDF page controls" {...(locked ? { inert: true, 'aria-disabled': 'true' } as Record<string, unknown> : {})}>
      <div class="row pdf-page-nav">{leading}{pages.length > 1 && <>
        <button class="secondary" disabled={!source || pageIndex === 0} aria-label="Previous PDF page" onClick={() => setPageIndex(i => i - 1)}>Previous</button>
        <label>Page<select aria-label="PDF page" value={String(pageIndex)} disabled={!source} onChange={e => setPageIndex(Number(e.currentTarget.value))}>{pages.map((_, index) => <option value={String(index)}>Page {index + 1} of {pages.length}</option>)}</select></label>
        <button class="secondary" disabled={!source || pageIndex >= pages.length - 1} aria-label="Next PDF page" onClick={() => setPageIndex(i => i + 1)}>Next</button>
      </>}</div>
      {tools}
      <div class="row pdf-zoom-buttons">
        <div class="segmented pdf-zoom-segment" role="group" aria-label="Zoom">
          <Tooltip text="Zoom out"><button class="segmented-button" aria-label="Zoom out" disabled={!source || scale <= steps[0] + .001} onClick={() => zoomTo([...steps].reverse().find(z => z < scale - .001) ?? steps[0])}><AppIcon name="zoom-out" /></button></Tooltip>
          <span class="segmented-divider" aria-hidden="true" />
          <Tooltip text="Zoom in"><button class="segmented-button" aria-label="Zoom in" disabled={!source || scale >= steps.at(-1)! - .001} onClick={() => zoomTo(steps.find(z => z > scale + .001) ?? steps.at(-1)!)}><AppIcon name="zoom-in" /></button></Tooltip>
        </div>
        {controls}
      </div>
    </div>
    {loading && <p role="status">Loading PDF page…</p>}
    {error && <div class="alert" role="alert"><p>{error}</p><button class="secondary" onClick={() => setRetry(i => i + 1)}>Retry blank source</button></div>}
    <div class="pdf-page-frame">{cover}<div class="pdf-page-scroll" ref={container}><div class="pdf-page-stage" ref={stage} style={viewport ? { width: viewport.width, height: viewport.height } : undefined}>
      <canvas ref={canvas} aria-hidden="true" style={{ visibility: loading || error ? 'hidden' : 'visible' }} />
      {viewport && source && <div class="pdf-overlay" style={{ '--pdf-scale': viewport.width / (pageViewport(source.pages[pageIndex]).width || viewport.width) }}>{overlay ? overlay({ viewport, pageIndex, source }) : (document?.placements ?? []).filter(p => p.pageIndex === pageIndex).map(p => <span key={p.id} class="pdf-placement" style={placementOverlayStyle(viewport, p.rect)} />)}</div>}
    </div></div></div>
    {/* The page image is hidden from screen readers, so its text is read to them instead; it isn't shown. */}
    {transcript && text && <p class="sr-only">{text}</p>}
  </div>;
}
